import { LineChart as FluentLineChart, type ChartProps, type LineChartPoints } from '@fluentui/react-charts';
import { makeStyles, tokens } from '@fluentui/react-components';
import { useMemo } from 'react';

import { seriesColor, toneToColor } from './colors';
import { tickFormatter } from './format';
import type { ChartTone, ChartUnit, Series } from './types';

interface LineChartProps {
  series: readonly Series[];
  yMax?: number;
  yMin?: number;
  unit?: ChartUnit;
  /** Status tint when there's a single series. */
  tone?: ChartTone;
  height?: number;
  hideLegend?: boolean;
  chartTitle?: string;
}

const useStyles = makeStyles({
  wrapper: {
    width: '100%',
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Multi-series line chart for latency/error-rate/throughput drilldowns. Each
 * series gets a stable color from the Fluent data-viz palette so two charts
 * sharing the same series order stay visually consistent.
 */
export function LineChart({
  series,
  yMax,
  yMin = 0,
  unit,
  tone,
  height = 200,
  hideLegend,
  chartTitle,
}: LineChartProps) {
  const classes = useStyles();
  const isSingle = series.length <= 1;

  const data: ChartProps = useMemo(() => {
    const lineChartData: LineChartPoints[] = series.map((s, i) => ({
      legend: s.name,
      color: s.color ?? (isSingle && tone ? toneToColor(tone) : seriesColor(i)),
      data: s.points.map((p) => ({
        x: p.t instanceof Date ? p.t : new Date(p.t),
        y: p.v,
      })),
    }));
    return {
      chartTitle,
      lineChartData,
    };
  }, [series, isSingle, tone, chartTitle]);

  const yMaxResolved = yMax ?? (unit === 'percent' ? 100 : undefined);
  // Caller wins; otherwise auto-hide the legend on single-series charts.
  const shouldHideLegend = hideLegend ?? isSingle;

  return (
    <div className={classes.wrapper}>
      <FluentLineChart
        data={data}
        height={height}
        yMinValue={yMin}
        yMaxValue={yMaxResolved}
        yAxisTickFormat={tickFormatter(unit)}
        hideLegend={shouldHideLegend}
        reflowProps={{ mode: 'min-width' }}
      />
    </div>
  );
}
